<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type { IPendingWithdrawalDenyFields } from '@modules/pending-withdrawals/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_PENDING_WITHDRAWALS } from '@modules/pending-withdrawals/constants';
import { usePendingWithdrawalsDenyMutation } from '@modules/pending-withdrawals/mutations';

import type {
  PendingWithdrawalDenyButtonEmits,
  PendingWithdrawalDenyButtonProps,
} from './types';

const props = withDefaults(defineProps<PendingWithdrawalDenyButtonProps>(), {});
const emit = defineEmits<PendingWithdrawalDenyButtonEmits>();

const { getTranslation } = useI18n();
const { confirmDelete } = useConfirmDialogs();
const { mutateAsync, isLoading } = usePendingWithdrawalsDenyMutation();

const isDialogVisible = ref(false);

function onDeny(event?: MouseEvent): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'DENIAL_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'DENIAL_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'DENIAL_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'DENIAL_NO').value,
    accept: () => {
      isDialogVisible.value = true;
    },
  });
}

function onModalHide(): void {
  isDialogVisible.value = false;
}

const form = ref<VFormRef>();
const schema = computed<FormSchema<IPendingWithdrawalDenyFields>>(() => ({
  comment: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'REASON_DENIAL_REASON').value,
  },
}));

async function onFormSubmit(values: IPendingWithdrawalDenyFields): Promise<void> {
  await mutateAsync({
    ...values,
    id: props.id,
  });

  onModalHide();

  emit('denied');
}

function onSubmit(): void {
  form.value?.submit();
}
</script>

<template>
  <VIconButton v-data-qa-auto-id="'PendingWithdrawalDenyButton'"
    v-if="isIcon"
    icon="pi-ban"
    severity="danger"
    :loading="isLoading"
    @click="onDeny"
  />
  <VButton
    v-else
    :label="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'VIEW_DENY')"
    severity="danger"
    :loading="isLoading"
    @click="onDeny()"
  />
  <VDialog
    :title="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'REASON_DENIAL_TITLE')"
    :visible="isDialogVisible"
    @hide="onModalHide"
  >
    <VForm
      ref="form"
      :schema="schema"
      @submit="onFormSubmit"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'REASON_DENIAL_CANCEL')"
        outlined
        @click="onModalHide"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'REASON_DENIAL_PROCEED')"
        @click="onSubmit"
      />
    </template>
  </VDialog>
</template>
